import axios from 'axios'
import { tokenRefreshed } from '../store/authSlice'

const API_URL = '/api'

// Injected by index.js after store creation to avoid circular dependency:
// api → store → authSlice → authService → api
let _store = null
export const injectStore = (store) => {
  _store = store
}

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Request interceptor to add token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => Promise.reject(error),
)

// ── Token refresh state ───────────────────────────────────────────────────────
// Prevents multiple simultaneous refresh calls when several requests fail at once.
let isRefreshing = false
let pendingQueue = [] // [{ resolve, reject }]

const processQueue = (error, token = null) => {
  pendingQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error)
    } else {
      resolve(token)
    }
  })
  pendingQueue = []
}

const forceLogout = () => {
  localStorage.removeItem('token')
  localStorage.removeItem('user')
  window.location.href = '/login'
}

// ── Response interceptor ──────────────────────────────────────────────────────
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const originalRequest = error.config

    // Only attempt refresh on 401 and only once per request (_retry flag)
    // Skip the refresh endpoint itself to avoid infinite loops
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url?.includes('/auth/refresh') &&
      !originalRequest.url?.includes('/auth/login')
    ) {
      // If a refresh is already in flight, queue this request
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          pendingQueue.push({ resolve, reject })
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`
            return api(originalRequest)
          })
          .catch((err) => Promise.reject(err))
      }

      originalRequest._retry = true
      isRefreshing = true

      return new Promise((resolve, reject) => {
        api
          .post('/auth/refresh')
          .then((response) => {
            const newToken = response.data?.access_token
            if (!newToken) throw new Error('No token in refresh response')

            localStorage.setItem('token', newToken)
            api.defaults.headers.common.Authorization = `Bearer ${newToken}`
            originalRequest.headers.Authorization = `Bearer ${newToken}`
            _store?.dispatch(tokenRefreshed(newToken))

            processQueue(null, newToken)
            resolve(api(originalRequest))
          })
          .catch((refreshError) => {
            processQueue(refreshError, null)
            forceLogout()
            reject(refreshError)
          })
          .finally(() => {
            isRefreshing = false
          })
      })
    }

    return Promise.reject(error)
  },
)

export default api
